import { SideModal } from "../../../components";
import { Button } from "../../../shared";
import { Autocomplete, Box, Chip, ChipDelete, Tooltip, Typography } from "@mui/joy";
import { Container, GitCompare, Settings } from "lucide-react";
import { useEffect, useState } from "react";

import { SupplierComparison as PriceComparison } from "../../../components/Supplier/PriceComparison";
import { useSupplier } from "../../../hooks/query";
import { useProject } from "../../../hooks";
import { useSelectedSupplier } from "./hooks";

interface ILaborSupplierComparison {
    selectedValues: Record<string, { quantity: number; unitPrice: number }>;
    setIsNextStageEligible: any;
}

export function LaborSupplierComparison(props: ILaborSupplierComparison) {
    const [openSupplierComparison, setOpenSupplierComparison] = useState(false);
    const [comparisonResults, setComparisonResults] = useState<any[]>([]);

    const { selectedValues, setIsNextStageEligible } = { ...props };

    const { project } = useProject();
    const { selectedSupplier } = useSelectedSupplier();
    const { useSupplierComparison } = useSupplier();
    const supplierComparisonSvc = useSupplierComparison();

    const handleOnCompare = async () => {
        if (!project?.id) return;
        try {
            const params = {
                ProjectId: project.id,
                SupplierNames: ["1st CR Roofing", "Beacon", "American Roofing Supply"],
            };

            const results = await supplierComparisonSvc.mutateAsync(params);
            setComparisonResults(results);
            setOpenSupplierComparison(true);
        } catch (error) {
            console.error("Error in supplier comparison:", error);
        }
    };

    useEffect(() => {
        setIsNextStageEligible({ isSupplierSelected: Boolean(selectedSupplier) });
    }, [selectedSupplier, setIsNextStageEligible]);

    const isSupplierSelectionEnabled =
        Object.keys(selectedValues).length > 0 && Object.values(selectedValues).some((v) => v.quantity > 0);

    return (
        <Box>
            <Box mt={2} gap={1}>
                <Typography level="body-sm" sx={{ color: "text.primary", fontWeight: 500 }}>
                    Supplier
                </Typography>
                <Typography level="body-xs">
                    Choose multiple suppliers to compare prices and make your selection
                </Typography>

                <Box display={"flex"} alignItems={"center"} gap={2} mt={1}>
                    {!selectedSupplier?.name && (
                        <Button
                            disabled={!isSupplierSelectionEnabled}
                            startDecorator={<GitCompare size={20} />}
                            onClick={handleOnCompare}
                            variant={"soft"}
                            color={"neutral"}
                        >
                            Compare & Choose
                        </Button>
                    )}
                    {selectedSupplier?.name && (
                        <Chip
                            variant="soft"
                            sx={{ borderRadius: "8px", border: "1px solid", borderColor: "divider", gap: 1 }}
                            size="lg"
                            color="neutral"
                            startDecorator={<Container size={16} />}
                            endDecorator={
                                <ChipDelete
                                    color="danger"
                                    variant="outlined"
                                    onClick={handleOnCompare}
                                    sx={{ borderRadius: 6, ml: 0 }}
                                >
                                    <Tooltip title={"Update Supplier"} arrow>
                                        <Settings size={"16px"} color="currentColor" />
                                    </Tooltip>
                                </ChipDelete>
                            }
                        >
                            {selectedSupplier?.name}
                        </Chip>
                    )}
                </Box>
            </Box>

            <SideModal
                portalBoxProps={{
                    minWidth: "60%",
                    width: 1000,
                    maxWidth: "100vw",
                }}
                open={openSupplierComparison}
                onClose={() => setOpenSupplierComparison(false)}
            >
                <PriceComparison comparisonResults={comparisonResults} />
            </SideModal>
        </Box>
    );
}
